import { Component, computed, inject, Injector, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import {
  OnyxDatePipe,
  OnyxDistancePipe,
  OnyxIconButtonComponent,
  OnyxInformationHeadingComponent,
  OnyxInformationListComponent,
  OnyxInformationRowComponent,
  OnyxTooltipDirective,
} from '@onyx/angular';
import { DateTime } from 'luxon';
import type { Subject } from 'rxjs';
import { AllowedCountriesComponent } from '../../../../../common/components/allowed-countries/allowed-countries.component';
import { AmountCellComponent } from '../../../../../common/components/cells/amount-cell/amount-cell.component';
import { CountryCellComponent } from '../../../../../common/components/cells/country-cell/country-cell.component';
import { DateRangeCellComponent } from '../../../../../common/components/cells/date-range-cell/date-range-cell.component';
import { StringCellComponent } from '../../../../../common/components/cells/string-cell/string-cell.component';
import { UnitCellComponent } from '../../../../../common/components/cells/unit-cell/unit-cell.component';
import { ModalDateRowComponent } from '../../../../../common/components/modal-documents/modal-date-row/modal-date-row.component';
import { DictionaryCode } from '../../../../../common/enums/dictionary-code';
import { CommonHelper } from '../../../../../common/helpers/common.helper';
import { DimensionsPipe } from '../../../../../common/pipes/dimensions.pipe';
import { DictionariesService } from '../../../../../common/services/dictionaries.service';
import { FleetCategory } from '../../../common/enums/fleet-category';
import { OwnershipType } from '../../../common/enums/ownership-type';
import type { Fleet } from '../../../common/interfaces/fleet';

@Component({
  selector: 'app-fleet-modal-details',
  imports: [
    OnyxInformationRowComponent,
    TranslatePipe,
    OnyxDatePipe,
    CountryCellComponent,
    UnitCellComponent,
    StringCellComponent,
    AmountCellComponent,
    OnyxDatePipe,
    DateRangeCellComponent,
    ModalDateRowComponent,
    OnyxDistancePipe,
    OnyxInformationListComponent,
    OnyxInformationHeadingComponent,
    OnyxIconButtonComponent,
    OnyxTooltipDirective,
    DimensionsPipe,
    AllowedCountriesComponent,
  ],
  templateUrl: './fleet-modal-details.component.html',
  styleUrl: './fleet-modal-details.component.scss',
})
export class FleetModalDetailsComponent {
  private readonly dictionariesService = inject(DictionariesService);
  private readonly router = inject(Router);
  public readonly injector = inject(Injector);

  public readonly vehicle = input.required<Fleet>();
  public readonly isVehicle = input.required<boolean>();
  public readonly close$ = input.required<Subject<void>>();

  protected readonly I18N = 'fleet.fleetModal';

  protected readonly DictionaryCode = DictionaryCode;
  protected readonly OwnershipType = OwnershipType;
  protected readonly FleetCategory = FleetCategory;

  protected fuelType = computed(() => {
    const fuelTypes = this.fuelTypes();
    if (!fuelTypes) return null;

    return (
      fuelTypes.find(
        (fuelType) =>
          fuelType.value === this.vehicle().fuelTankCapacity?.fuelType,
      ) ?? null
    );
  });

  protected isTachographLegalizationExpired = computed(() => {
    const legalizationDate =
      this.vehicle().tachograph?.nextLegalizationDate ?? null;
    if (!legalizationDate) return false;

    return DateTime.fromISO(legalizationDate).diffNow().toMillis() <= 0;
  });

  private fuelTypes = toSignal(
    this.dictionariesService.getDictionary(DictionaryCode.FUEL_TYPE),
    { initialValue: null },
  );

  protected scheduleService(): void {
    CommonHelper.showOnMap(this.router, this.vehicle().uuid, this.injector, {
      showService: true,
    });
    this.close$().next();
  }
}
